import React from 'react';
import { RegisterOptions, useForm } from 'react-hook-form';
import { CreateEditSnippetModalView } from './CreateEditSnippetModal.view';
import { FormOptions } from 'constants/form';
import type {
  CoreSnippetFieldsFragment,
  CreateSnippetInput,
  CreateSnippetMutationFn,
  UpdateSnippetMutationFn
} from 'models/graphql';
import { contentValidationRules, nameValidationRules } from './constants/validationRules';
import { MAX_CONTENT_LENGTH, MAX_HASHTAGS_COUNT } from './constants/common';
import { UpdateSnippetInput } from 'models/graphql';
import { triggerErrorToast } from 'helpers/triggerErrorToast';
import { useSelectedOrganization } from 'hooks/useSelectedOrganization';
import { UseFormProps } from 'react-hook-form/dist/types/form';

export type CreateEditSnippetModalControllerProps = {
  mode: 'create' | 'edit';
  onClose: VoidFunction;
  snippetToEdit?: CoreSnippetFieldsFragment | null;
  updateSnippetMutation: UpdateSnippetMutationFn;
  createSnippetMutation: CreateSnippetMutationFn;
  loading: boolean;
  defaultName?: string;
  defaultContent?: string;
  nameRules?: RegisterOptions;
  contentRules?: RegisterOptions;
  // those 2 will be used by counter in textarea
  maxHashtagCount?: number;
  maxContentLength?: number;
};

export const CreateEditSnippetModalController = (props: CreateEditSnippetModalControllerProps): JSX.Element => {
  const { selectedWorkspace } = useSelectedOrganization();

  const {
    mode,
    onClose,
    loading,
    updateSnippetMutation,
    createSnippetMutation,
    snippetToEdit,
    defaultName,
    defaultContent,
    nameRules = nameValidationRules,
    contentRules = contentValidationRules,
    maxHashtagCount = MAX_HASHTAGS_COUNT,
    maxContentLength = MAX_CONTENT_LENGTH
  } = props;

  const formOptions = { ...FormOptions } as UseFormProps<CreateSnippetInput>;
  const form = useForm<CreateSnippetInput>({
    ...formOptions,
    defaultValues: { name: defaultName ?? '', content: defaultContent ?? '' }
  });

  const createSnippet = async (input: CreateSnippetInput) => {
    if (!input.name || !input.content) return;

    await createSnippetMutation({
      variables: {
        workspaceId: selectedWorkspace?.id as string,
        input
      }
    })
      .then(onClose)
      .catch(triggerErrorToast('Create Snippet Error'));
  };

  const updateSnippet = async (input: UpdateSnippetInput) => {
    if ((!input.name && !input.content) || !snippetToEdit) return;

    if (input.name === snippetToEdit.name && input.content === snippetToEdit.content) {
      onClose();
      return;
    }

    await updateSnippetMutation({
      variables: {
        snippetId: snippetToEdit.id,
        input
      }
    })
      .then(onClose)
      .catch(triggerErrorToast('Update Snippet Error'));
  };

  const onFormSubmit = (data: CreateSnippetInput) => (mode === 'create' ? createSnippet : updateSnippet)(data);
  const formTitle = mode === 'create' ? 'Create a Snippet' : 'Edit Snippet';

  return (
    <CreateEditSnippetModalView
      formTitle={formTitle}
      form={form}
      onSubmit={onFormSubmit}
      onClose={onClose}
      loading={loading}
      nameRules={nameRules}
      contentRules={contentRules}
      maxHashtagCount={maxHashtagCount}
      maxContentLength={maxContentLength}
    />
  );
};
